"use strict";

/**
 * SuperSageAuditLogController
 *
 * Super-Admin dashboard view of the SAGE admin action audit log (unlock,
 * regenerate, etc) for a chosen project. Moved out of the developer
 * dashboard in WEB-3413 iter-22+ when the backend endpoint relocated
 * under /sec/super/sage-reports/audit-log and the permission was
 * renamed to the SUPER_* prefix in iter-24 to align with the new
 * Super-tier dashboard placement.
 *
 * Backend (iter-22+):
 *   GET /sec/super/sage-reports/audit-log
 *   query: property_id (required), action, user_id, from, to,
 *          report_type, per_page
 *
 * Permission gate (iter-24): `SUPER_SAGE_EXPORT_AUDIT_LOGS_VIEW`.
 *   - Super users hold it implicitly via hasUserPermission's
 *     short-circuit.
 *   - Non-Super users see the screen only when an admin explicitly
 *     grants the permission (rare — the page is Super-Admin tier).
 *
 * Project list: scoped to LISTED projects the user has DMS_VW_PROJ on.
 *
 * Response envelope (iter-20 onwards): standard SSResponse —
 *   { success, code, sub_code, data: { audit_log: [...], pagination: {...} }, message }
 * SSHttpService handles the unwrap.
 */
class SuperSageAuditLogController {

    constructor($scope, SSHttpService, SSAlertService, SSUserService, SSUtilService, $timeout) {
        const self = this;

        self.$scope = $scope;
        self.SSHttpService = SSHttpService;
        self.SSAlertService = SSAlertService;
        self.SSUserService = SSUserService;
        self.SSUtilService = SSUtilService;
        self.$timeout = $timeout;

        // Super-Admin permission gate (iter-24+).
        //   - SUPER role users hold this implicitly via SSUserService.hasPermission's
        //     `user.role === 'SUPER'` short-circuit (services/user.js:426-427) —
        //     this matches Waseem's iter-23 spec: "Super users hold it implicitly".
        //   - Non-Super users see the screen only when an admin explicitly grants
        //     the SUPER_SAGE_EXPORT_AUDIT_LOGS_VIEW permission (rare — page is
        //     Super-tier).
        // The `isSuper()` OR is belt-and-suspenders: it makes the Super-admin
        // path explicit at the call site so a future refactor of hasPermission()
        // can't accidentally lock Super out. There is no separate
        // ADD / UPDATE / DELETE permission — audit logs are append-only
        // (iter-23 explicit).
        self.PERMISSION = 'SUPER_SAGE_EXPORT_AUDIT_LOGS_VIEW';
        self.has_access = self.SSUserService.isSuper()
                       || self.SSUserService.hasPermission(self.PERMISSION);

        self.REPORT_TYPE_INVOICE = 'sage_invoice';
        self.REPORT_TYPE_RECEIPT = 'sage_receipt';

        self.properties = null;
        self.selectedPropertyId = null;
        self.filters = self._defaultFilters();
        self.rows = [];
        self.total = 0;
        self.page = 1;
        self.perPage = 25;
        self.lastPage = 1;
        self.loading = false;
        self.searched = false;
        self.datepickerOptions = { showWeeks: false };
        self.displayDateFrom = false;
        self.displayDateTo = false;

        if (self.has_access) {
            self.init();
        }
    }

    init() {
        const self = this;
        self.prepareDeveloperProperties();
        self.initDatepickers();
    }

    /**
     * Wire the From / To Date inputs to jQuery UI datepicker — same widget
     * used by the Sales & Payments / SPA Dates pages for visual consistency.
     * `onSelect` pushes the picked value back into ng-model via $scope.$apply
     * because jQuery UI fires its select callback outside Angular's digest.
     */
    initDatepickers() {
        const self = this;
        self.$timeout(function () {
            $('#sageAuditLogFromDate').datepicker({
                changeMonth: true,
                changeYear: true,
                dateFormat: 'dd/mm/yy',
                onSelect: function (dateText) {
                    self.$scope.$apply(function () { self.filters.from = dateText; });
                }
            });
            $('#sageAuditLogToDate').datepicker({
                changeMonth: true,
                changeYear: true,
                dateFormat: 'dd/mm/yy',
                onSelect: function (dateText) {
                    self.$scope.$apply(function () { self.filters.to = dateText; });
                }
            });
        });
    }

    _defaultFilters() {
        return {
            action: '',
            user_id: '',
            report_type: '',
            from: null,
            to: null
        };
    }

    /**
     * Project picker source. The audit log lives on the Super-Admin
     * dashboard, but it's still project-scoped — the user picks one
     * project to filter the log entries.
     *
     * Super users get the **unscoped** Super properties endpoint
     * (`sec/super/properties`) — they have implicit access to every
     * project on the platform and should NEVER see an empty dropdown
     * because their DMS permissions weren't set up. Non-Super users
     * (rare: held the explicit permission grant) fall back to the
     * DMS_VW_PROJ-scoped developer endpoint.
     *
     * Status field naming differs between the two endpoints:
     *   - sec/super/properties        → row.property_status
     *   - sec/developer/properties/list → row.status
     * Filter accordingly when narrowing to LISTED only — matches the
     * scope of the SAGE Export / History pages so the audit log shows
     * the same set of projects as the rest of the SAGE suite.
     *
     * `lastPropertyId` is shared across SAGE pages so the same project
     * stays selected as the user moves between them.
     */
    prepareDeveloperProperties() {
        const self = this;

        let isSuper = self.SSUserService.isSuper();
        let url     = isSuper ? 'sec/super/properties' : 'sec/developer/properties/list';
        let params  = isSuper ? null : { status: SHOWSUITE.PROPERTY_STATUS_LISTED };

        loading(true);
        self.SSHttpService.getAPIRequest(url, params).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            let all = (response.data && response.data.properties) || [];
            // The two endpoints return overlapping but differently-named
            // fields. Normalise to `{ id, name, status }` so the rest of
            // the controller + the Blade's `ng-options="p.id as p.name"`
            // don't have to branch on which endpoint replied.
            //   sec/super/properties        → { id, property_name, property_status, ... }
            //   sec/developer/properties/list → { id, name,          status,          ... }
            self.properties = all
                .map(function (p) {
                    if (!p) { return null; }
                    return {
                        id:     p.id,
                        name:   isSuper ? (p.property_name   || p.name)   : (p.name   || p.property_name),
                        status: isSuper ? (p.property_status || p.status) : (p.status || p.property_status),
                        // preserve the raw row in case downstream code wants it
                        _raw:   p
                    };
                })
                .filter(function (p) {
                    return p && p.status === SHOWSUITE.PROPERTY_STATUS_LISTED;
                });

            let saved = self.SSUtilService.getFromLocalStorage('lastPropertyId');
            if (saved && self.properties.some(p => String(p.id) === String(saved))) {
                self.selectedPropertyId = parseInt(saved, 10);
            }

            self.$timeout(function () {
                if ($('select.dashboard-select#property-select').niceSelect) {
                    $('select.dashboard-select#property-select').niceSelect('update');
                }
            });
        });
    }

    // -- View handlers -------------------------------------------------------

    onPropertyChange() {
        const self = this;
        self._resetResults();
        if (self.selectedPropertyId) {
            self.SSUtilService.saveToLocalStorage('lastPropertyId', self.selectedPropertyId);
        }
    }

    openDateFrom() { this.displayDateFrom = true; }
    openDateTo() { this.displayDateTo = true; }

    _resetResults() {
        const self = this;
        self.rows = [];
        self.total = 0;
        self.page = 1;
        self.searched = false;
    }

    /**
     * User-triggered search — fetch page 1 with current filters.
     */
    search() {
        const self = this;
        if (!self.selectedPropertyId) {
            self.SSAlertService.danger('Validation Error', 'Please select a project first.');
            return;
        }
        self.page = 1;
        self.fetch();
    }

    fetch() {
        const self = this;

        if (!self.selectedPropertyId) { return; }

        let params = {
            property_id: self.selectedPropertyId,
            per_page: self.perPage,
            page: self.page
        };
        if (self.filters.action)      { params.action      = self.filters.action; }
        if (self.filters.user_id)     { params.user_id     = self.filters.user_id; }
        if (self.filters.report_type) { params.report_type = self.filters.report_type; }
        if (self.filters.from)        { params.from        = self._fmtDate(self.filters.from); }
        if (self.filters.to)          { params.to          = self._fmtDate(self.filters.to); }

        self.loading = true;
        loading(true);

        self.SSHttpService.getAPIRequest('sec/super/sage-reports/audit-log', params).then(function (response) {
            loading(false);
            self.loading = false;
            self.searched = true;

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            // Iter-20 envelope: response.data is { audit_log, pagination }.
            let inner      = (response && response.data) || {};
            let pagination = inner.pagination || {};

            self.rows     = inner.audit_log || [];
            self.total    = pagination.total        || self.rows.length;
            self.page     = pagination.current_page || self.page;
            self.perPage  = pagination.per_page     || self.perPage;
            self.lastPage = pagination.last_page    || 1;
        });
    }

    // -- Display helpers -----------------------------------------------------

    /** SAGE_REPORT_UNLOCK → "Unlock", title-cased for unknown actions. */
    formatAction(action) {
        if (!action) { return ''; }
        let s = String(action).replace(/^SAGE_REPORT_/, '').replace(/_/g, ' ').toLowerCase();
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    /** sage_invoice → Invoice, sage_receipt → Receipt. */
    formatReportType(reportType) {
        if (!reportType) { return ''; }
        if (reportType === this.REPORT_TYPE_INVOICE) { return 'Invoice'; }
        if (reportType === this.REPORT_TYPE_RECEIPT) { return 'Receipt'; }
        return reportType;
    }

    /** Read a key out of the row's `changeset` JSON (parsed lazily). */
    cs(row, key) {
        if (!row || !row.changeset) { return null; }
        if (typeof row.changeset === 'string') {
            try { row.changeset = JSON.parse(row.changeset); } catch (e) { return null; }
        }
        return row.changeset[key];
    }

    // -- Pagination ----------------------------------------------------------

    nextPage() {
        const self = this;
        if (self.page * self.perPage >= self.total) { return; }
        self.page += 1;
        self.fetch();
    }

    prevPage() {
        const self = this;
        if (self.page <= 1) { return; }
        self.page -= 1;
        self.fetch();
    }

    /**
     * Backend wants `yyyy-mm-dd`. jQuery UI datepicker pushes a `dd/mm/yyyy`
     * string into ng-model on select; legacy uib-datepicker pushed a Date
     * object. Handle all three.
     */
    _fmtDate(d) {
        if (!d) { return null; }
        if (typeof d === 'string') {
            let parts = d.split('/');
            if (parts.length === 3 && parts[2].length === 4) {
                return parts[2] + '-' + parts[1] + '-' + parts[0];
            }
            return d;
        }
        let pad = (n) => (n < 10 ? '0' + n : '' + n);
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    }
}

SuperSageAuditLogController.$inject = [
    '$scope',
    'SSHttpService',
    'SSAlertService',
    'SSUserService',
    'SSUtilService',
    '$timeout'
];
app.controller('SuperSageAuditLogController', SuperSageAuditLogController);
